// Card lateral para mostrar/editar perfil de comprador

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { User, Eye, DollarSign, Building, Home, Edit } from 'lucide-react';
import BuyerProfileWizard from './BuyerProfileWizard';

/**
 * Card lateral que muestra el perfil del comprador
 * @param {object} profile - Datos del perfil
 * @param {function} onUpdate - Callback al actualizar perfil
 * @param {string} variant - 'default' | 'compact' (nuevo)
 */
// Maps from DB values to i18n keys
const PAYMENT_KEYS = {
  cash: 'profile.labels.payment.cash',
  finance_under_60: 'profile.labels.payment.financeUnder60',
  finance_60_80: 'profile.labels.payment.finance6080',
  finance_over_80: 'profile.labels.payment.financeOver80',
  undecided: 'profile.labels.payment.undecided',
};
const FINANCING_KEYS = {
  preapproved: 'profile.labels.financing.preapproved',
  in_process: 'profile.labels.financing.inProcess',
  not_started: 'profile.labels.financing.notStarted',
  no_need: 'profile.labels.financing.noNeed',
};
const SELL_KEYS = {
  no: 'common.no',
  signed_deposit: 'profile.labels.sell.signedDeposit',
  on_sale: 'profile.labels.sell.onSale',
  not_yet_listed: 'profile.labels.sell.notListed',
};
const FEATURE_KEYS = {
  required: 'profile.labels.features.required',
  desirable: 'profile.labels.features.desirable',
  not_needed: 'profile.labels.features.notNeeded',
};

export const BuyerProfileCard = ({ profile, onUpdate, variant = 'default' }) => {
  const { t } = useTranslation();
  const [showEditModal, setShowEditModal] = useState(false);

  const hasProfile = !!profile;

  const handleUpdate = async (profileData) => {
    await onUpdate(profileData);
    setShowEditModal(false);
  };

  // Calcular progreso (si hay perfil)
  const hasFeatures = hasProfile && !!(
    profile.minBedrooms || profile.minBathrooms || profile.desiredSqm || 
    profile.outdoorSpace || profile.reformLevel || profile.elevator || profile.parking
  );
  const totalAnswered = hasProfile ? (3 + (hasFeatures ? 1 : 0)) : 0;

  // ============================================
  // ✅ VARIANTE: COMPACT (para fase OPEN)
  // ============================================
  if (variant === 'compact') {
    // Si no hay perfil: mostrar badge de advertencia
    if (!hasProfile) {
      return (
        <>
          <div className="bg-amber-500/10 backdrop-blur-xl rounded-lg px-3 py-2 border border-amber-500/20 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 flex-1">
              <User className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
              <span className="text-xs text-amber-300 font-medium">
                {t('profile.compact.incomplete')}
              </span>
            </div>
            <button
              onClick={() => setShowEditModal(true)}
              className="text-xs bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 font-medium px-3 py-1 rounded transition-colors whitespace-nowrap"
            >
              {t('profile.compact.complete')}
            </button>
          </div>

          {showEditModal && (
            <BuyerProfileWizard
              isOpen={showEditModal}
              onClose={() => setShowEditModal(false)}
              onSubmit={handleUpdate}
              isEditing={false}
            />
          )}
        </>
      );
    }

    // Si hay perfil: mostrar badge compacto con estado
    const isComplete = totalAnswered === 4;
    return (
      <>
        <div className={`backdrop-blur-xl rounded-lg px-3 py-2 border flex items-center justify-between gap-2 ${
          isComplete 
            ? 'bg-emerald-500/10 border-emerald-500/20' 
            : 'bg-blue-500/10 border-blue-500/20'
        }`}>
          <div className="flex items-center gap-2 flex-1">
            <User className={`w-3.5 h-3.5 flex-shrink-0 ${
              isComplete ? 'text-emerald-400' : 'text-blue-400'
            }`} />
            <span className={`text-xs font-medium ${
              isComplete ? 'text-emerald-300' : 'text-blue-300'
            }`}>
              {isComplete
                ? t('profile.compact.statusComplete', { total: totalAnswered })
                : t('profile.compact.statusPartial', { total: totalAnswered })}
            </span>
          </div>
          <button
            onClick={() => setShowEditModal(true)}
            className={`text-xs font-medium px-3 py-1 rounded transition-colors whitespace-nowrap flex items-center gap-1 ${
              isComplete
                ? 'bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300'
                : 'bg-blue-500/20 hover:bg-blue-500/30 text-blue-300'
            }`}
          >
            <Edit className="w-3 h-3" />
            {t('common.edit')}
          </button>
        </div>

        {showEditModal && (
          <BuyerProfileWizard
            isOpen={showEditModal}
            onClose={() => setShowEditModal(false)}
            onSubmit={handleUpdate}
            initialData={profile}
            isEditing={true}
          />
        )}
      </>
    );
  }

  // ============================================
  // VARIANTE DEFAULT (comportamiento original)
  // ============================================
  
  // ESTADO VACÍO
  if (!hasProfile) {
    return (
      <>
        <div className="bg-blue-500/10 backdrop-blur-xl rounded-xl p-3 border border-blue-500/20 min-h-[114px] flex flex-col">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-blue-400" />
              <h3 className="text-sm font-semibold text-white">{t('profile.title')}</h3>
            </div>
          </div>

          <p className="text-xs text-slate-400 mb-2 flex-1">
            {t('profile.emptyDesc')}
          </p>

          <button
            onClick={() => setShowEditModal(true)}
            className="w-full text-xs bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 font-medium py-2 rounded-lg transition-colors"
          >
            {t('profile.completeNow')}
          </button>
        </div>

        {showEditModal && (
          <BuyerProfileWizard
            isOpen={showEditModal}
            onClose={() => setShowEditModal(false)}
            onSubmit={handleUpdate}
            isEditing={false}
          />
        )}
      </>
    );
  }

  // ============================================
  // ESTADO COMPLETO (DEFAULT) - CON OJO Y HOVER
  // ============================================
  return (
    <>
      <div className="bg-emerald-500/10 backdrop-blur-xl rounded-xl p-3 border border-emerald-500/20 min-h-[114px] flex flex-col">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-emerald-400" />
            <h3 className="text-sm font-semibold text-white">{t('profile.title')}</h3>
          </div>

          {/* Ojo con hover para ver detalles */}
          <div className="relative group/eye">
            <button
              className="rounded-lg hover:bg-slate-700/50 transition-colors p-1"
              aria-label={t('profile.title')}
            >
              <Eye className="w-3.5 h-3.5 text-emerald-400 hover:text-emerald-300 transition-colors" />
            </button>

            {/* Tooltip - aparece arriba en hover */}
            <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 bg-slate-800 border border-emerald-500/30 rounded-xl p-3 shadow-2xl opacity-0 invisible group-hover/eye:opacity-100 group-hover/eye:visible transition-all duration-200 z-[150] pointer-events-none">
              {/* Flechita */}
              <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-slate-800"></div>

              <div className="space-y-2">
                {/* Método de pago */}
                <div className="flex items-start gap-2">
                  <DollarSign className="w-3.5 h-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-xs text-slate-500">{t('profile.tooltip.payment')}</p>
                    <p className="text-xs text-slate-300 font-medium">
                      {t(PAYMENT_KEYS[profile.paymentMethod])}
                    </p>
                  </div>
                </div>

                {/* Financiación */}
                <div className="flex items-start gap-2">
                  <Building className="w-3.5 h-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-xs text-slate-500">{t('profile.tooltip.financing')}</p>
                    <p className="text-xs text-slate-300 font-medium">
                      {t(FINANCING_KEYS[profile.financingStatus])}
                    </p>
                  </div>
                </div>

                {/* Vender vivienda */}
                <div className="flex items-start gap-2">
                  <Home className="w-3.5 h-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-xs text-slate-500">{t('profile.tooltip.sellProperty')}</p>
                    <p className="text-xs text-slate-300 font-medium">
                      {t(SELL_KEYS[profile.propertyToSell])}
                    </p>
                  </div>
                </div>

                {/* Características buscadas */}
                {hasFeatures && (
                  <div className="pt-2 border-t border-slate-700">
                    <p className="text-xs text-slate-500 mb-1">{t('profile.tooltip.lookingFor')}</p>
                    <div className="flex flex-wrap gap-1">
                      {profile.minBedrooms && (
                        <span className="text-xs bg-emerald-500/10 text-emerald-300 px-1.5 py-0.5 rounded">
                          {t('profile.tooltip.bedrooms', { count: profile.minBedrooms })}
                        </span>
                      )}
                      {profile.minBathrooms && (
                        <span className="text-xs bg-emerald-500/10 text-emerald-300 px-1.5 py-0.5 rounded">
                          {t('profile.tooltip.bathrooms', { count: profile.minBathrooms })}
                        </span>
                      )}
                      {profile.desiredSqm && (
                        <span className="text-xs bg-emerald-500/10 text-emerald-300 px-1.5 py-0.5 rounded">
                          {profile.desiredSqm}m²
                        </span>
                      )}
                      {profile.outdoorSpace && (
                        <span className="text-xs bg-emerald-500/10 text-emerald-300 px-1.5 py-0.5 rounded">
                          {t('profile.tooltip.terrace', { option: t(FEATURE_KEYS[profile.outdoorSpace]) })}
                        </span>
                      )}
                      {profile.parking && (
                        <span className="text-xs bg-emerald-500/10 text-emerald-300 px-1.5 py-0.5 rounded">
                          {t('profile.tooltip.parking', { option: t(FEATURE_KEYS[profile.parking]) })}
                        </span>
                      )}
                      {profile.elevator && (
                        <span className="text-xs bg-emerald-500/10 text-emerald-300 px-1.5 py-0.5 rounded">
                          {t('profile.tooltip.elevator', { option: t(FEATURE_KEYS[profile.elevator]) })}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <p className="text-xs text-emerald-300 mb-2 flex-1">
          {t('profile.registered', { total: totalAnswered })}
        </p>

        <button
          onClick={() => setShowEditModal(true)}
          className="w-full text-xs bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 font-medium py-2 rounded-lg transition-colors"
        >
          {t('profile.editProfile')}
        </button>
      </div>

      {showEditModal && (
        <BuyerProfileWizard
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSubmit={handleUpdate}
          initialData={profile}
          isEditing={true}
        />
      )}
    </>
  );
};

export default BuyerProfileCard;